import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Spinner from '@oberoninternal/travelbase-ds/components/figure/Spinner';
import Switch from '@oberoninternal/travelbase-ds/components/form/Switch';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import MapGL from 'react-map-gl';
import styled, { css } from 'styled-components';
import { StringParam } from 'use-query-params';
import useSupercluster from 'use-supercluster';
import { ZOOM_DEFAULT, ZOOM_MAX, ZOOM_MIN } from '../constants/map';
import searchParams from '../constants/searchParams';
import { useTenantContext } from '../context/TenantContext';
import { SearchHitFragment } from '../generated/graphql';
import useBounds, { getBoundsForHits } from '../hooks/search/useBounds';
import useMapSize from '../hooks/search/useMapSize';
import usePoints from '../hooks/search/usePoints';
import useQueryParams from '../hooks/useQueryParams';
import { ResultsProps } from './SearchResults';
import SearchHit from './SearchHit';
import SearchMapClusters from './SearchMapClusters';

import useBodyClass from '../hooks/useBodyClass';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import Info from '@oberoninternal/travelbase-ds/components/figure/Info';
import type { MapRef } from 'react-map-gl';
import { BBox } from 'geojson';
import ZoomButtons from './ZoomButtons';

const SearchMap: FC<React.PropsWithChildren<ResultsProps>> = ({
    data,
    activeSpecial,
    unitParams,
    isLoading,
    pageCount,
}) => {
    const hits = useMemo(() => data ?? [], [data]);
    const { brandConfig } = useTenantContext();
    const mapRef = useRef<MapRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const deviceSize = useDeviceSize();

    const [defaultZoom, minZoom, maxZoom] = useMemo(
        () =>
            [
                brandConfig.mapZoom ?? ZOOM_DEFAULT,
                (deviceSize === 'laptop' || deviceSize === 'desktop'
                    ? brandConfig.mapMinZoom?.desktop
                    : brandConfig.mapMinZoom?.mobile) ?? ZOOM_MIN,
                ZOOM_MAX,
            ] as const,
        [brandConfig.mapMinZoom?.desktop, brandConfig.mapMinZoom?.mobile, brandConfig.mapZoom, deviceSize]
    );
    const getViewport = useCallback(() => {
        if (hits?.length) {
            return getBoundsForHits(hits, defaultZoom);
        }
        return {
            ...brandConfig.coordinates,
            zoom: defaultZoom,
        };
    }, [brandConfig.coordinates, defaultZoom, hits]);
    const bounds = useBounds(mapRef);
    const points = usePoints(hits ?? []);
    const [viewport, setViewport] = useState(getViewport);
    const zoom = viewport.zoom ?? defaultZoom;
    const { clusters, supercluster } = useSupercluster({
        points,
        bounds: bounds as BBox,
        zoom: Math.min(zoom, maxZoom),
        options: { radius: 48, maxZoom },
    });

    const [searchOnMapInteraction, setSearchOnMapInteraction] = useState(true);
    const [showOnMapInteraction, setShowOnMapInteraction] = useState(true);

    const [params, setParams] = useQueryParams({ ...searchParams, activeId: StringParam });
    const [mapBoxLoading, setMapBoxLoading] = useState(true);

    const setBBx = useCallback(() => {
        if (mapBoxLoading || !bounds?.length) {
            return;
        }
        const [swLon, swLat, neLon, neLat] = bounds;
        setParams({ bbBottom: swLat, bbTop: neLat, bbLeft: swLon, bbRight: neLon });
    }, [bounds, mapBoxLoading, setParams]);

    const viewportSet = useRef(false);
    // After doing the search query we'll set the viewport accordingly
    useEffect(() => {
        if (!viewportSet.current && hits?.length) {
            setViewport(vp => ({ ...vp, ...getViewport() }));
            viewportSet.current = true;
        }
    }, [getViewport, hits]);

    const size = useMapSize();

    const activeSpecialId = typeof activeSpecial !== 'string' ? activeSpecial?.special.id : undefined;
    const renderSearchHit = useCallback(
        (hit: SearchHitFragment) => (
            <SearchHit
                unitParams={unitParams}
                style={{ cursor: 'pointer' }}
                rentalUnit={hit.rentalUnit}
                trips={hit.trips}
                activeSpecial={activeSpecialId}
            />
        ),
        [activeSpecialId, unitParams]
    );
    useBodyClass('no-vertical-scroll-on-mobile');
    return (
        <Container ref={containerRef}>
            {size !== null && (
                <MapGL
                    onClick={e => {
                        // only close the active balloon if the target is the map ('overlays')
                        if (e.target.getContainer().className === 'overlays' && params.activeId) {
                            setParams({ activeId: undefined });
                        }
                    }}
                    onLoad={() => setMapBoxLoading(false)}
                    dragRotate={false}
                    style={{ position: 'sticky', top: size.top, height: size.height }}
                    mapStyle="mapbox://styles/mapbox/outdoors-v11"
                    mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_KEY ?? ''}
                    ref={mapRef}
                    onMoveEnd={() => {
                        if (searchOnMapInteraction) {
                            setBBx();
                        } else {
                            setShowOnMapInteraction(false);
                        }
                    }}
                    onMove={s => {
                        setViewport(s.viewState);

                        if (
                            params.activeId &&
                            !s.target.isEasing() &&
                            (s.target.isRotating() || s.target.isZooming())
                        ) {
                            setParams({ activeId: undefined });
                        }
                    }}
                    maxZoom={maxZoom}
                    minZoom={minZoom}
                    initialViewState={viewport}
                    {...viewport}
                >
                    {!showOnMapInteraction && (
                        <SearchButton
                            className="gt-s"
                            variant="outline"
                            onClick={() => {
                                setBBx();
                                setShowOnMapInteraction(true);
                            }}
                        >
                            <FormattedMessage defaultMessage="Op kaart zoeken" />
                        </SearchButton>
                    )}
                    {showOnMapInteraction && (
                        <InteractionActions className="gt-s">
                            {isLoading && <Spinner />}
                            {!isLoading && (
                                <>
                                    {showOnMapInteraction && (
                                        <>
                                            <Switch
                                                checked={searchOnMapInteraction}
                                                id="search-map-on-interaction"
                                                onChange={e => setSearchOnMapInteraction(e.currentTarget.checked)}
                                            />
                                            <label htmlFor="search-map-on-interaction">
                                                <Body variant="small">
                                                    <FormattedMessage defaultMessage="Zoeken bij verschuiven kaart" />
                                                </Body>
                                            </label>
                                        </>
                                    )}
                                </>
                            )}
                        </InteractionActions>
                    )}
                    {pageCount > 1 && (
                        <LimitedResultsContainer>
                            <LimitedResultsMessage>
                                <Body variant="small">
                                    <Info style={{ position: 'relative', top: '0.2rem', marginRight: '0.3rem' }} />{' '}
                                    <FormattedMessage
                                        defaultMessage="Je ziet de {amount} meest relevante resultaten in dit gebied. Zoom in of gebruik de filters om andere resultaten te krijgen."
                                        values={{
                                            amount: hits.length,
                                        }}
                                    />
                                </Body>
                            </LimitedResultsMessage>
                        </LimitedResultsContainer>
                    )}
                    <SearchMapClusters
                        clusters={clusters}
                        supercluster={supercluster}
                        activeId={params.activeId}
                        setActiveId={activeId => setParams({ activeId: activeId ?? undefined })}
                        renderSearchHit={renderSearchHit}
                    />
                    <ZoomButtons />
                </MapGL>
            )}
        </Container>
    );
};

const commonStyles = css`
    position: absolute;
    box-shadow: 0 1.6rem 2.4rem 0 rgba(59, 118, 160, 0.07), 0 2.4rem 8rem 0 rgba(59, 118, 160, 0.1);
    z-index: 4;
    top: 4rem;
    right: var(--wrapperPadding);
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        right: unset;
        left: 50%;
        transform: translateX(-50%);
        top: 2rem;
    }
`;

const SearchButton = styled(Button)`
    ${commonStyles}
`;

const InteractionActions = styled.div`
    ${commonStyles}
    min-width: 20rem;
    justify-content: center;
    align-items: center;
    background: white;
    display: flex;
    padding: 0.8rem;
    border-radius: ${({ theme }) => theme.radius.button};
    height: 4.8rem;

    > * + * {
        margin-left: 0.8rem;
    }

    label {
        margin-bottom: 0;
    }
`;

const LimitedResultsContainer = styled.div`
    position: absolute;
    bottom: 8rem;
    right: var(--wrapperPadding);
    left: var(--wrapperPadding);
    z-index: 4;
    display: flex;
    justify-content: center;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        bottom: 10rem;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        bottom: 4rem;
    }
`;

const LimitedResultsMessage = styled.div`
    background: white;
    display: flex;
    padding: 0.8rem;
    border-radius: ${({ theme }) => theme.radius.button};
    box-shadow: 0 1.6rem 2.4rem 0 rgba(59, 118, 160, 0.07), 0 2.4rem 8rem 0 rgba(59, 118, 160, 0.1);
`;

const Container = styled.div`
    --marker-radius: 2.4rem;
    --marker-diameter: calc(var(--marker-radius) * 2);
    flex: 1;
    position: relative;
    min-height: 640px;

    /* mapbox styles */
    .mapboxgl-popup-anchor-top,
    .mapboxgl-popup-anchor-top-right,
    .mapboxgl-popup-anchor-top-left {
        margin-top: var(--marker-diameter);
    }

    .mapboxgl-popup-anchor-right {
        margin-top: var(--marker-radius);
    }
    .mapboxgl-popup-anchor-left {
        margin-top: var(--marker-radius);
        margin-left: var(--marker-radius);
    }

    .mapboxgl-popup-content {
        padding: 0;
        border-radius: 0;
        box-shadow: none;
        background: unset;
    }

    .mapboxgl-popup {
        z-index: 1;
    }
`;

export default SearchMap;
